import { useCallback, useRef, useState } from 'react';
import { reelCredits } from 'fym-shared/src/music24/music24Plans';
import { useCreateReelMutation } from '../../../../../redux/api/fymAPI';
import { ErrorAPIResult, isSuccessfulResult } from '../../../../../redux/api/utils/isSuccessfulResult';
import { useSMLRouter } from '../../../../../routing/sml/hooks';
import { SMLRoutes } from '../../../../../routing/sml/SMLRoutes';
import { aiReelStudioParams } from '../../studioRoute';
import { CreateReelOptions, SerializedReel } from '../../types';
import { ReelDraft } from './useReelDraft';
import { reelRefusalCode, ReelRefusalCode, reelSongRefusal, ReelSongRefusal } from './reelRefusal';
import { useVideoCreditGate } from '../../credits/useVideoCreditGate';

/**
 * True when the form values are the ones the reel was made from. The reel does not carry its
 * song or lyrics, so the caller also checks that the reel came from this exact draft.
 */
export function draftMatchesReel(options: CreateReelOptions, reel: SerializedReel): boolean {
  // Design z6: the reel has no waveform, voice or length choice, so the song decides.
  return options.catalogItemUuid === reel.catalogItemUuid;
}

interface UseReelActionsProps {
  draft: ReelDraft;
  /** The `?reel=` uuid on screen; empty on the form. */
  reelUuid: string;
}

/**
 * The actions of the AI reel theme of the Studio: Generate, and the buttons of a failed reel (design b3, b4).
 * "Try again" posts the form again only when the reel on screen came from the current form
 * values. For any other reel (a deep link, a reel from the history) it opens the form with the
 * values of that reel, so it never spends a unit on the wrong release or song.
 *
 * A reel costs one video credit. When the balance (plan plus packs) is 0, Generate and "Try again"
 * open the credit packs at once and send nothing (owner rule 2026-10-06); a 402 from the server
 * opens the same packs (`customBaseQuery`).
 */
export function useReelActions({ draft, reelUuid }: UseReelActionsProps) {
  const router = useSMLRouter();
  const [createReel, { isLoading: isGenerating, error: createError, reset: resetCreate }] = useCreateReelMutation();
  const { canSpend } = useVideoCreditGate(draft.accountId);
  const [refusal, setRefusal] = useState<ReelRefusalCode | null>(null);
  // A 400 M24_NO_MASTER or M24_MASTER_PROCESSING: the song field asks for the master again.
  const [songRefusal, setSongRefusal] = useState<ReelSongRefusal | null>(null);
  // A failed "Try again" that is not a refusal (a 5xx or a network error) stays on the reel page,
  // so that page shows the error. It belongs to the reel it was made from.
  const [retryError, setRetryError] = useState<{ reelUuid: string; error: ErrorAPIResult['error'] } | null>(null);
  // The reels made from one `createOptions` object. A new object means the form changed.
  const madeFromDraft = useRef<{ options?: CreateReelOptions; uuids: string[] }>({ uuids: [] });

  const { createOptions, fillFromReel } = draft;

  const create = useCallback(
    async (options: CreateReelOptions): Promise<void> => {
      setRefusal(null);
      setSongRefusal(null);
      setRetryError(null);
      if (!canSpend(reelCredits())) {
        return;
      }
      const result = await createReel(options);
      if (isSuccessfulResult(result)) {
        const { uuid } = result.data.reel;
        const made = madeFromDraft.current;
        madeFromDraft.current = { options, uuids: made.options === options ? [...made.uuids, uuid] : [uuid] };
        router.push(
          SMLRoutes.promoteStudio,
          aiReelStudioParams({ catalogItemUuid: options.catalogItemUuid, reel: uuid })
        );
        return;
      }
      // 402 opens the upgrade prompt from customBaseQuery; 403 and 503 show their own alert. A
      // refused "Try again" goes back to the form, where the alerts show. Any other failed "Try
      // again" stays on the reel page, which shows the error (`tryAgainError`).
      const code = reelRefusalCode(result.error);
      setRefusal(code);
      setSongRefusal(reelSongRefusal(result.error));
      if (reelUuid === '') {
        return;
      }
      if (code === null) {
        setRetryError({ reelUuid, error: result.error });
        return;
      }
      router.push(SMLRoutes.promoteStudio, aiReelStudioParams({ catalogItemUuid: options.catalogItemUuid }));
    },
    [canSpend, createReel, reelUuid, router]
  );

  const generate = useCallback(() => {
    if (createOptions !== undefined) {
      create(createOptions).catch(console.error);
    }
  }, [create, createOptions]);

  const openForm = useCallback(
    (reel: SerializedReel) => {
      fillFromReel(reel);
      // The Studio reads the song from the URL, so the form opens on the song of the reel.
      router.push(
        SMLRoutes.promoteStudio,
        aiReelStudioParams(reel.catalogItemUuid === null ? {} : { catalogItemUuid: reel.catalogItemUuid })
      );
    },
    [fillFromReel, router]
  );

  // The song is the master of the catalog row (D27): "Change the song" opens the form, where the
  // user picks another release. The master itself is replaced only in the catalog.
  const changeSong = openForm;

  const tryAgain = useCallback(
    (reel: SerializedReel) => {
      const made = madeFromDraft.current;
      const isFromDraft =
        createOptions !== undefined &&
        made.options === createOptions &&
        made.uuids.includes(reel.uuid) &&
        draftMatchesReel(createOptions, reel);
      if (isFromDraft) {
        create(createOptions).catch(console.error);
        return;
      }
      openForm(reel);
    },
    [create, createOptions, openForm]
  );

  /**
   * Hides the error of a failed Generate (the rail shows it under the button) when the user changes
   * the form. A refusal (the plan, the song) keeps its own notice.
   */
  const clearCreateError = useCallback(() => {
    if (createError !== undefined && refusal === null && songRefusal === null) {
      resetCreate();
    }
  }, [createError, refusal, resetCreate, songRefusal]);

  /** The error of a failed "Try again" on the reel page that is on screen now. */
  const tryAgainError = retryError !== null && retryError.reelUuid === reelUuid ? retryError.error : undefined;

  return {
    generate,
    tryAgain,
    openForm,
    changeSong,
    isGenerating,
    createError,
    clearCreateError,
    refusal,
    songRefusal,
    tryAgainError,
  };
}
